import {Component, EventEmitter, inject, Output} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {ImportModulePreflightResult} from '../../../core/definitions/import-module';
import {MediaOnMissing} from '../import-payload';
import {ImportSessionStore} from '../import-session.store';
import {MEDIA_VALUES_TITLE_KEY} from '../live-preview/live-preview';
import {ImportView} from '../step-bar/import-steps';
import {
  cardLabels,
  PlacementOutlook,
  RowSkipOutlook,
  runOutlook,
  valueLabel,
} from '../preflight-result/preflight-warnings';
import {rowCountText} from '../row-count';

/** A placement card's outcome with the card's title as the card shows
 * it. */
export interface TitledPlacementOutlook extends PlacementOutlook {
  title: string | null;
}

/** A field whose values the run skips, by the label the preflight rows
 * show for it (a field of the linked media under «Bilde»), with the rows
 * that hold one. */
export interface SkippedFieldLine {
  label: string;
  rows: number;
}

/** What the run card's "Kjøringen vil" lists from the shown preflight. */
export interface RunStepOutlook {
  created: number;
  skippedRows: RowSkipOutlook[];
  skippedRowsTotal: number;
  placements: TitledPlacementOutlook[];
  skipped: SkippedFieldLine[];
  skippedCards: number;
}

/** The rows a Forhåndskontroll is limited to; `last` is `null` for a
 * range that runs to the table's end. */
export interface PreflightRowRange {
  first: number;
  last: number | null;
}

/** The key that names a reason the run skips rows, with the count. */
export const ROW_SKIP_KEYS: Record<RowSkipOutlook['reason'], string> = {
  media_already_linked: 'TRANS__IMPORT__RUN_OUTLOOK_SKIP_MEDIA_ALREADY_LINKED',
  media_partly_linked: 'TRANS__IMPORT__RUN_OUTLOOK_SKIP_MEDIA_PARTLY_LINKED',
  media_shared_file: 'TRANS__IMPORT__RUN_OUTLOOK_SKIP_MEDIA_SHARED_FILE',
  media_ambiguous: 'TRANS__IMPORT__RUN_OUTLOOK_SKIP_MEDIA_AMBIGUOUS',
  media_unmatched: 'TRANS__IMPORT__RUN_OUTLOOK_SKIP_MEDIA_UNMATCHED',
  id_collision: 'TRANS__IMPORT__RUN_OUTLOOK_SKIP_ID_COLLISION',
  id_invalid: 'TRANS__IMPORT__RUN_OUTLOOK_SKIP_ID_INVALID',
};

/**
 * The Import module's Kontroll og kjøring step: the preflight of the
 * mapping and its result, then the run with its options, what it will do,
 * its progress and its result.
 */
@Component({
  selector: 'app-import-run-step',
  templateUrl: './import-run-step.component.html',
  styleUrls: ['./import-run-step.component.scss'],
  standalone: false,
})
export class ImportRunStepComponent {
  readonly store: ImportSessionStore = inject(ImportSessionStore);
  readonly rowSkipKeys = ROW_SKIP_KEYS;
  private readonly translate = inject(TranslateService);

  /** Asks for the value review. */
  @Output() openValueReview = new EventEmitter<void>();

  private outlookMemo: {
    result: ImportModulePreflightResult; onMissing: MediaOnMissing; outlook: RunStepOutlook;
  } | null = null;

  /** The rows of the run range as the card shows them, "≈ 356" for an
   * approximate count; `null` while not known. */
  get runRangeRowsText(): string | null {
    const count = this.store.runRangeCount;
    return count ? rowCountText(count) : null;
  }

  /** The run range as the Forhåndskontroll card names it, with row 1 for
   * a blank **Fra rad** and `last: null` for a blank **Til rad**; `null`
   * while no range is picked. */
  get preflightRowRange(): PreflightRowRange | null {
    if (!this.store.runRangeChosen) {
      return null;
    }
    return {
      first: this.store.runFirstRowControl.value || 1,
      last: this.store.runLastRowControl.value || null,
    };
  }

  /** Opens the step view where a preflight warning is fixed. */
  openView(view: ImportView): void {
    this.store.viewRequested.next(view);
  }

  /** What the shown preflight says the run does, built once per result
   * and media policy; `null` while it did not check the whole run range. */
  get outlook(): RunStepOutlook | null {
    const result = this.store.preflightCoversRunRange ? this.store.preflightResult : null;
    if (!result) {
      return null;
    }
    const onMissing = this.store.mediaOnMissingControl.value;
    const memo = this.outlookMemo;
    if (memo?.result !== result || memo.onMissing !== onMissing) {
      this.outlookMemo = {result, onMissing, outlook: this.buildOutlook(result, onMissing)};
    }
    return this.outlookMemo!.outlook;
  }

  private buildOutlook(result: ImportModulePreflightResult, onMissing: MediaOnMissing): RunStepOutlook {
    const outlook = runOutlook(result, this.store.isPlacementGroupKey, onMissing, this.store.placementRequiredFieldId);
    const labels = cardLabels(result, this.store.cardListTitle, this.store.subBlockListTitle);
    const labelOf = valueLabel(labels, this.translate.instant(MEDIA_VALUES_TITLE_KEY));
    return {
      created: outlook.created,
      skippedRows: outlook.skippedRows,
      skippedRowsTotal: outlook.skippedRows.reduce((sum, r) => sum + r.rows, 0),
      placements: outlook.placements.map(p => ({...p, title: labels.title(p.key)})),
      skipped: outlook.skipped.map(field => ({
        label: labelOf(field.label, field.eventKey, field.on, field.subBlock), rows: field.rows,
      })),
      skippedCards: outlook.skippedCards,
    };
  }
}
